/**
 * Backgammon Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * and multiplayer stats.
 */

import {
  BackgammonStatistics,
  BackgammonGameResult,
  BackgammonGameStats,
  BackgammonDifficulty,
  BackgammonGameSettings,
  PlayerSide,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class BackgammonStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'backgammon_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): BackgammonStatistics {
    return {
      singlePlayer: {
        easy: this.createEmptyGameStats(),
        medium: this.createEmptyGameStats(),
        hard: this.createEmptyGameStats(),
      },
      localMultiplayer: this.createEmptyGameStats(),
      onlineMultiplayer: this.createEmptyGameStats(),
      totalGames: 0,
      totalPlayTime: 0,
    };
  }

  private createEmptyGameStats(): BackgammonGameStats {
    return {
      wins: 0,
      losses: 0,
      draws: 0,
      winPercentage: 0,
      averageMoves: 0,
      totalCheckersHit: 0,
      totalGammons: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): BackgammonStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as BackgammonStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get backgammon statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(
    result: BackgammonGameResult,
    playerSide: PlayerSide,
    userId?: string
  ): boolean {
    try {
      const stats = this.getStatistics(userId);

      if (result.mode === 'singleplayer' && result.difficulty) {
        this.updateGameStats(stats.singlePlayer[result.difficulty], result, playerSide);
      } else if (result.mode === 'local-multiplayer') {
        this.updateGameStats(stats.localMultiplayer, result, playerSide);
      } else if (result.mode === 'multiplayer-realtime') {
        this.updateGameStats(stats.onlineMultiplayer, result, playerSide);
      }

      stats.totalGames++;
      stats.totalPlayTime += result.duration;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record backgammon game result:', error);
      return false;
    }
  }

  private updateGameStats(
    stats: BackgammonGameStats,
    result: BackgammonGameResult,
    playerSide: PlayerSide
  ): void {
    const totalGames = stats.wins + stats.losses + stats.draws;

    if (result.winner === playerSide) {
      stats.wins++;
    } else if (result.winner) {
      stats.losses++;
    } else {
      stats.draws++;
    }

    const newTotalGames = stats.wins + stats.losses + stats.draws;
    stats.winPercentage = newTotalGames > 0 ? (stats.wins / newTotalGames) * 100 : 0;

    // Update average moves
    stats.averageMoves =
      newTotalGames > 0
        ? (stats.averageMoves * totalGames + result.totalMoves) / newTotalGames
        : result.totalMoves;

    // Update total hits
    stats.totalCheckersHit += result.totalHits;

    // Update gammons
    if (result.isGammon) {
      stats.totalGammons++;
    }
  }

  /** Get statistics for a specific mode and difficulty. */
  getModeStatistics(
    mode: BackgammonGameSettings['mode'],
    difficulty?: BackgammonDifficulty,
    userId?: string
  ): BackgammonGameStats {
    const stats = this.getStatistics(userId);

    if (mode === 'singleplayer' && difficulty) {
      return stats.singlePlayer[difficulty];
    } else if (mode === 'local-multiplayer') {
      return stats.localMultiplayer;
    } else if (mode === 'multiplayer-realtime') {
      return stats.onlineMultiplayer;
    }

    return this.createEmptyGameStats();
  }

  /** Reset all statistics. */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset backgammon statistics:', error);
      return false;
    }
  }
}
